import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  TreeDeciduous, ArrowLeft, Plus, Calendar, Heart, 
  Baby, Skull, Star, Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const eventIcons = {
  birth: <Baby className="w-5 h-5 text-success" />,
  death: <Skull className="w-5 h-5 text-muted-foreground" />,
  marriage: <Heart className="w-5 h-5 text-accent" />,
  historical: <Star className="w-5 h-5 text-warning" />,
  custom: <Calendar className="w-5 h-5 text-info" />
};

const eventColors = {
  birth: "bg-success/10 border-success/20",
  death: "bg-muted border-border",
  marriage: "bg-accent/10 border-accent/20",
  historical: "bg-warning/10 border-warning/20",
  custom: "bg-info/10 border-info/20"
};

const TimelinePage = ({ user }) => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    event_type: "custom",
    event_date: "",
    title: "",
    description: "",
    member_id: ""
  });

  useEffect(() => {
    fetchData();
  }, [branchId]);

  const fetchData = async () => {
    try {
      const [eventsRes, membersRes, branchRes] = await Promise.all([
        fetch(`${API}/events?branch_id=${branchId}`, { credentials: "include" }),
        fetch(`${API}/members?branch_id=${branchId}`, { credentials: "include" }),
        fetch(`${API}/branches/${branchId}`, { credentials: "include" })
      ]);

      if (!branchRes.ok) {
        toast.error("Branch not found");
        navigate("/dashboard");
        return;
      }

      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setEvents(eventsData);
      }

      if (membersRes.ok) {
        const membersData = await membersRes.json();
        setMembers(membersData);
      }

      const branchData = await branchRes.json();
      setBranch(branchData);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load timeline");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...newEvent, branch_id: branchId })
      });

      if (!response.ok) throw new Error("Failed to add event");

      const event = await response.json();
      setEvents([event, ...events].sort((a, b) => 
        new Date(b.event_date) - new Date(a.event_date)
      ));
      setAddEventOpen(false);
      setNewEvent({
        event_type: "custom",
        event_date: "",
        title: "",
        description: "",
        member_id: ""
      });
      toast.success("Event added to timeline!");
    } catch (error) {
      console.error("Error adding event:", error);
      toast.error("Failed to add event");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const response = await fetch(`${API}/events/${eventId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (!response.ok) throw new Error("Failed to delete event");

      setEvents(events.filter(e => e.event_id !== eventId));
      toast.success("Event removed");
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    }
  };

  // Group events by year
  const eventsByYear = events.reduce((acc, event) => {
    const year = event.event_date?.split("-")[0] || "Unknown";
    if (!acc[year]) acc[year] = [];
    acc[year].push(event);
    return acc;
  }, {});

  const years = Object.keys(eventsByYear).sort((a, b) => Number(b) - Number(a));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/branch/${branchId}`)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <TreeDeciduous className="w-8 h-8 text-primary" />
              <span className="font-display text-xl font-bold text-foreground">Matambanadzo-Gurundoro</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-2">
            Timeline
          </h1>
          <p className="text-lg text-muted-foreground">
            {branch?.name} family events and milestones
          </p>
        </div>

        {/* Add Event Button */}
        <div className="mb-8">
          <Dialog open={addEventOpen} onOpenChange={setAddEventOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-primary-foreground rounded-full"
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Add Timeline Event</DialogTitle>
                <DialogDescription>
                  Record an important family event or milestone.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddEvent} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="event-type">Event Type</Label>
                  <Select
                    value={newEvent.event_type}
                    onValueChange={(value) => setNewEvent({ ...newEvent, event_type: value })}
                  >
                    <SelectTrigger id="event-type" data-testid="event-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="birth">Birth</SelectItem>
                      <SelectItem value="death">Death</SelectItem>
                      <SelectItem value="marriage">Marriage</SelectItem>
                      <SelectItem value="historical">Historical</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event-date">Date *</Label>
                  <Input
                    id="event-date"
                    type="date"
                    data-testid="event-date-input"
                    value={newEvent.event_date}
                    onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event-title">Title *</Label>
                  <Input
                    id="event-title"
                    data-testid="event-title-input"
                    placeholder="e.g., John's Birth"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event-member">Related Member (Optional)</Label>
                  <Select
                    value={newEvent.member_id || "none"}
                    onValueChange={(value) => setNewEvent({ ...newEvent, member_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger id="event-member" data-testid="event-member-select">
                      <SelectValue placeholder="Select member" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {members.map((m) => (
                        <SelectItem key={m.member_id} value={m.member_id}>
                          {m.first_name} {m.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event-description">Description (Optional)</Label>
                  <Textarea
                    id="event-description"
                    data-testid="event-description-input"
                    placeholder="Add more details..."
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setAddEventOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="submit-event-btn">
                    Add Event
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Timeline */}
        {events.length === 0 ? (
          <Card className="bg-card rounded-2xl border border-border/60 p-12 text-center">
            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-display text-xl font-medium text-foreground mb-2">
              No events yet
            </h3>
            <p className="text-muted-foreground mb-6">
              Start recording your family's important milestones
            </p>
            <Button 
              onClick={() => setAddEventOpen(true)}
              className="bg-primary text-primary-foreground rounded-full"
              data-testid="empty-add-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add First Event
            </Button>
          </Card>
        ) : (
          <div className="space-y-10">
            {years.map((year) => (
              <div key={year}>
                <h2 className="font-display text-2xl font-semibold text-foreground mb-6 sticky top-20 bg-background/80 backdrop-blur-sm py-2">
                  {year}
                </h2>
                <div className="space-y-4 relative">
                  {/* Timeline line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
                  
                  {eventsByYear[year].map((event) => {
                    const relatedMember = members.find(m => m.member_id === event.member_id);
                    
                    return (
                      <Card 
                        key={event.event_id}
                        className={`ml-12 bg-card rounded-2xl border shadow-sm ${eventColors[event.event_type] || eventColors.custom}`}
                        data-testid={`event-card-${event.event_id}`}
                      >
                        {/* Timeline dot */}
                        <div className="absolute -left-6 mt-6 w-3 h-3 bg-background border-2 border-primary rounded-full"></div>
                        
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className="mt-1">
                                {eventIcons[event.event_type] || eventIcons.custom}
                              </div>
                              <div>
                                <h3 className="font-display text-lg font-semibold text-foreground">
                                  {event.title}
                                </h3>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {new Date(event.event_date).toLocaleDateString('en-US', {
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                  {relatedMember && (
                                    <span className="ml-2">
                                      · {relatedMember.first_name} {relatedMember.last_name}
                                    </span>
                                  )}
                                </p>
                                {event.description && (
                                  <p className="text-foreground leading-relaxed">
                                    {event.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDeleteEvent(event.event_id)}
                              data-testid={`delete-event-${event.event_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default TimelinePage;
